import { useState } from 'react'
import { Alert, Button, Dialog, Text } from '@/ui'
import { TypographyStyles } from '@/styles'
import {
  type ActivateAs,
  FinancialAccount,
  FinancialType,
} from '@/reducers/finances/financialAccountsReducer'
import {
  reactivateFinancialAccount,
  REACTIVATE_FINANCIAL_ACCOUNT_KEY,
} from '@/actions/financialAccountActions'
import { setPlaidTarget } from '@/reducers/finances/plaidLauncherReducer'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { selectFinancialAccountById } from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'

type AccountNameProps = Pick<
  FinancialAccount,
  'plaidInstitutionName' | 'subtype' | 'mask'
>

const AccountName = ({
  plaidInstitutionName,
  subtype,
  mask,
}: AccountNameProps) => (
  <strong>
    {plaidInstitutionName} {subtype} ••••{mask}
  </strong>
)

const PersonalBody = ({ account }: { account: FinancialAccount }) => (
  <>
    <Text variant={TypographyStyles.BODY_SM}>
      Need to switch <AccountName {...account} /> to pull in business
      transactions? Remember to review your account for any personal
      transactions.
    </Text>
    <Text variant={TypographyStyles.BODY_SM}>
      You may need to reauthorize through Plaid to get started.
    </Text>
  </>
)

const InactiveBusinessBody = () => (
  <Text variant={TypographyStyles.BODY_SM}>
    To start pulling in transactions automatically, we may need you to
    reauthorize your bank account through Plaid.
  </Text>
)

const ManualBody = ({ account }: { account: FinancialAccount }) => (
  <Text variant={TypographyStyles.BODY_SM}>
    We will add <AccountName {...account} /> back to your list of active
    accounts.
  </Text>
)

const ReactivateBodyRouter = ({
  account,
  activateAs,
}: {
  account: FinancialAccount
  activateAs: ActivateAs
}) => {
  switch (true) {
    case account.accountType === FinancialType.MANUAL:
      return <ManualBody account={account} />
    case activateAs === 'business' && account.markedPersonalAt !== null:
      return <PersonalBody account={account} />
    case activateAs === 'personal':
      return <ManualBody account={account} />
    default:
      return <InactiveBusinessBody />
  }
}

const getTitle = (account: FinancialAccount, activateAs: ActivateAs) =>
  activateAs === 'business' && account.markedPersonalAt !== null
    ? 'Reactivate as business account'
    : 'Reactivate account'

export const ReactivateAccountModal = ({
  accountId,
  activateAs,
  onClose,
}: {
  accountId: number
  activateAs: ActivateAs
  onClose: () => void
}) => {
  const dispatch = useAppDispatch()
  const account = useReselector(selectFinancialAccountById, accountId)
  const isLoading = useReselector(
    getIsFetching,
    REACTIVATE_FINANCIAL_ACCOUNT_KEY
  )
  const [attempted, setAttempted] = useState(false)
  const error = useReselector(getFetchError, REACTIVATE_FINANCIAL_ACCOUNT_KEY)

  if (!account) return null

  const handleReactivate = async () => {
    setAttempted(true)
    const result = await dispatch(
      reactivateFinancialAccount(account.id, activateAs)
    )
    if (result) {
      onClose()
      if (result.needsPlaidLink) {
        dispatch(setPlaidTarget({ accountId: account.id, mode: 'reconnect' }))
      }
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content onDismiss={onClose}>
        <Text variant={TypographyStyles.H1}>
          {getTitle(account, activateAs)}
        </Text>
        <div className="flex flex-col gap-2">
          <ReactivateBodyRouter account={account} activateAs={activateAs} />
          {attempted && error && (
            <Alert variant="error" title="Couldn't reactivate account">
              {error.message}
            </Alert>
          )}
        </div>
        <Dialog.Footer className="sm:space-x-0">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button loading={isLoading} onClick={handleReactivate}>
            Reactivate
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
